import * as React from 'react'
import type { ApiLink, Collection, CollectionPage, MediaEntry, MoviePage } from './api-types'

/**
 * The catalogue's first-level collections — the categories the browse pages offer as
 * navigation. Same-origin, so a plain root-relative path (the session cookie rides
 * along). Answers a {@link CollectionPage} whose `results` are the *root*
 * collections only; sub-collections are reached through a movie's `rootline`.
 */
export const COLLECTION_DISCOVER_ENDPOINT = '/api/3.0/content/collection/discover'

/**
 * One collection by id. Carries the collection's `name` and a small sample of
 * `related_movies` — a handful of titles, NOT the collection's contents. The full,
 * paged listing comes from {@link catalogEndpoint}.
 */
export const collectionEndpoint = (id: string): string => `/api/3.0/content/collection/${id}`

/**
 * One collection's *child* collections — the second level of the catalogue tree,
 * which {@link COLLECTION_DISCOVER_ENDPOINT} (roots only) doesn't reach.
 *
 * Answers a {@link CollectionPage} shaped exactly like the root slot's. Note the
 * backend answered this route with the parent collection object itself, not a page
 * of children, when this was written — hence {@link fetchCollectionChildren}
 * reading `results` defensively: an answer without one is "no children", so the
 * second-level navigation stays hidden until the endpoint carries them.
 */
export const collectionChildrenEndpoint = (id: string): string =>
  `/api/3.0/content/collection/${id}/discover`

/**
 * The child collections of one collection, or `[]` for a leaf.
 *
 * Never rejects: sub-navigation is an extra on top of a page that works without it,
 * so a failure is logged and read as "no children" rather than surfaced.
 */
export const fetchCollectionChildren = async (id: string): Promise<Collection[]> => {
  const url = collectionChildrenEndpoint(id)
  try {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`${url} answered ${res.status}`)
    const json = await res.json() as Partial<CollectionPage>
    // A collection with no children may answer with something that isn't a page at
    // all, so the array — not the request succeeding — is what says there are any.
    return Array.isArray(json.results) ? json.results : []
  } catch (error) {
    console.error(error)
    return []
  }
}

/** The in-app route of the catalogue index — every title, with the categories on top. */
export const COLLECTIONS_PATH = '/videos/categories'

/**
 * The in-app route of a collection's own page (`/videos/category/:id`, registered by
 * the browse pilet). Lives next to the endpoints so the showcase rows, the category
 * navigation and the page itself all spell a collection's address one way — the
 * styleguide's own components link here too, which is why it can't sit in a pilet.
 */
export const collectionPath = (id: string): string => `/videos/category/${id}`

/**
 * Titles per page in the catalogue grid. Sized to fill a wide TV grid several rows
 * deep, so a viewer on a remote turns pages as rarely as possible.
 */
export const CATALOG_PAGE_SIZE = 48

/**
 * How the catalogue endpoint may order a listing. These three are the whole of it —
 * anything else is rejected with a 500 rather than ignored, so this type is the
 * guard that keeps a typo out of the query string.
 *
 * `alpha` currently answers in DESCENDING name order (Z→A), and the endpoint takes
 * no direction parameter to flip it. That is the backend's to fix; the catalogue
 * asks for `alpha` regardless, because re-sorting what arrives would only order the
 * page in hand and silently lie about the pages either side of it.
 */
export type CatalogSort = 'alpha' | 'recent' | 'popularity'

/** How the catalogue grid orders itself: by name, as an index should. */
export const CATALOG_DEFAULT_SORT: CatalogSort = 'alpha'

/**
 * A page of catalogue titles, optionally narrowed to one collection.
 *
 * `limit` is not optional in practice: without it the API answers with the whole
 * catalogue in one go AND reports `total_pages: 0`, so passing it is what makes the
 * response pageable at all. `category` matches the collection and its descendants.
 */
export const catalogEndpoint = (
  { category, page = 1, limit = CATALOG_PAGE_SIZE, sort = CATALOG_DEFAULT_SORT }: {
    category?: string | null,
    page?: number,
    limit?: number,
    sort?: CatalogSort,
  }
): string => {
  const params = new URLSearchParams({ limit: String(limit), page: String(page), sort })
  if (category) params.set('category', category)
  return `/api/3.0/content/movie/discover?${params.toString()}`
}

/**
 * The collection id behind a `rel: 'self'` link (`/api/3.0/content/collection/<id>`).
 *
 * The showcase configuration sends the id as its own field, so this is only the
 * fallback for the endpoints that identify a collection by its link alone. Returns
 * `null` when the links point somewhere that isn't a collection, so a caller can
 * decide not to render a link at all rather than build a broken one.
 */
export const collectionIdFromLinks = (links?: ApiLink[]): string | null => {
  const self = links?.find(link => link.rel === 'self')
  const match = self?.href.match(/\/content\/collection\/([^/?#]+)/)
  return match ? match[1] : null
}

export interface CollectionsState {
  /** The first-level collections — empty while loading and when there are none. */
  collections: Collection[];
  loading: boolean;
}

export interface CollectionState {
  /** The collection, or `null` while loading and when the id is unknown. */
  collection: Collection | null;
  loading: boolean;
  /** The id doesn't resolve — the page reports it rather than showing an empty grid. */
  failed: boolean;
}

export interface CatalogPageState {
  /** The titles on the requested page. */
  movies: MediaEntry[];
  /**
   * How many pages there are, or `null` when the API can't be trusted to say.
   *
   * It can't whenever a `category` is in play: the API applies the filter to
   * `results` but computes `total_results`/`total_pages` over the *whole* catalogue,
   * so a category holding 4 titles still reports the catalogue's 9. Rather than
   * print a wrong total we print none, and let {@link CatalogPageState.hasNext}
   * drive the pager.
   */
  totalPages: number | null;
  /** Total matching titles, on the same terms as {@link CatalogPageState.totalPages}. */
  total: number | null;
  /**
   * Whether there is a page after this one. Derived from the page being full rather
   * than from the counters, so it stays right for a filtered listing — and right at
   * the end of an unfiltered one, where `total_pages` floors (9 titles at 2 per page
   * reports 4 pages, while page 5 genuinely holds the ninth).
   */
  hasNext: boolean;
  loading: boolean;
  /** The request failed outright — the page shows an error rather than "no titles". */
  failed: boolean;
}

/**
 * Load the first-level collections. Degrades to an empty list after logging: the
 * category navigation is an extra, and must never take its page down with it.
 */
export const useCollections = (): CollectionsState => {
  const [state, setState] = React.useState<CollectionsState>({ collections: [], loading: true })

  React.useEffect(() => {
    // Guard state writes behind `active` so a response landing after unmount doesn't
    // set state on a gone component.
    let active = true
    fetch(COLLECTION_DISCOVER_ENDPOINT)
      .then(res => {
        if (res.status === 404) return null
        if (!res.ok) throw new Error(`${COLLECTION_DISCOVER_ENDPOINT} answered ${res.status}`)
        return res.json() as Promise<CollectionPage>
      })
      .then(page => { if (active) setState({ collections: page?.results ?? [], loading: false }) })
      .catch(error => {
        console.error(error)
        if (active) setState({ collections: [], loading: false })
      })
    return () => { active = false }
  }, [])

  return state
}

/**
 * Load one collection — what names a collection's page and its breadcrumb.
 *
 * Fetched rather than picked out of {@link useCollections}: the discover slot lists
 * the first-level collections only, so a page reached for a sub-collection (a
 * showcase row can be configured on one) would otherwise have no name to show.
 * Pass `null` for the unfiltered catalogue index, which has no collection at all.
 */
export const useCollection = (id: string | null): CollectionState => {
  const [state, setState] = React.useState<CollectionState>({ collection: null, loading: id !== null, failed: false })

  React.useEffect(() => {
    if (id === null) {
      setState({ collection: null, loading: false, failed: false })
      return
    }
    // Guard behind `active`: a slow fetch for a previous id must not resolve after
    // navigation and overwrite the current collection.
    let active = true
    setState({ collection: null, loading: true, failed: false })
    fetch(collectionEndpoint(id))
      .then(res => {
        // fetch only rejects on a network failure, so an unknown id would otherwise
        // sail through as a 404 body and render as a nameless page.
        if (!res.ok) throw new Error(`${collectionEndpoint(id)} answered ${res.status}`)
        return res.json() as Promise<Collection>
      })
      .then(collection => { if (active) setState({ collection, loading: false, failed: false }) })
      .catch(error => {
        console.error(error)
        if (active) setState({ collection: null, loading: false, failed: true })
      })
    return () => { active = false }
  }, [id])

  return state
}

/** Every collection a title is filed under: its main category and its alternatives. */
const categoriesOf = (movie: MediaEntry): Collection[] =>
  [movie.main_category, ...(movie.alternative_categories ?? [])]
    .filter((category): category is Collection => !!category)

/**
 * The direct children of `parentId`, read off the `rootline` each category on a
 * listing's titles carries (`[…, parent, child]`).
 *
 * This is the catalogue tree as the data exposes it today: `collection/:id/discover`
 * answers with the collection itself rather than a page of children (checked against
 * the running backend), and the root `discover` slot returns the roots only — so a
 * listing's own titles are the only place the second level is legible. Because
 * `movie/discover?category=X` matches X *and its descendants*, a parent's listing
 * names every child that holds a title.
 *
 * Sorted by name: rootlines are read in the order titles happen to arrive, which
 * would otherwise reshuffle the navigation from one page of the listing to the next.
 */
export const subCollectionsOf = (movies: MediaEntry[], parentId: string): Collection[] => {
  const children = new Map<string, Collection>()
  movies.forEach(movie => categoriesOf(movie).forEach(category => {
    const line = category.rootline ?? []
    const index = line.findIndex(entry => entry.id === parentId)
    const child = index >= 0 ? line[index + 1] : undefined
    if (child && !children.has(child.id)) children.set(child.id, child)
  }))
  return [...children.values()].sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * The ancestors of `id`, root first, from the same rootlines — `[]` for a root and
 * whenever no title on the listing is filed under it.
 *
 * What tells a deep collection's page where it sits: the URL (`/videos/category/:id`)
 * is spelled identically at every level, and the collection resource carries no
 * rootline of its own.
 */
export const collectionRootline = (movies: MediaEntry[], id: string): Collection[] => {
  for (const movie of movies) {
    for (const category of categoriesOf(movie)) {
      const line = category.rootline ?? []
      const index = line.findIndex(entry => entry.id === id)
      if (index >= 0) return line.slice(0, index)
    }
  }
  return []
}

export interface SubCollectionsState {
  /**
   * The collections to offer as the second level: the current collection's own
   * children, or — when it *is* a child — its siblings, so the row doesn't vanish the
   * moment a viewer picks one of its entries. Empty while loading, and for a leaf
   * whose place in the tree no title reveals.
   */
  items: Collection[];
  /**
   * Which collection {@link SubCollectionsState.items} hangs off — the current one on
   * a parent's page, its parent on a child's page. `null` when there is no second
   * level.
   */
  parentId: string | null;
  /**
   * The current collection's ancestors, root first. The first-level tabs mark
   * `rootline[0]` so a deep collection keeps its root lit, and the breadcrumb walks
   * the whole chain.
   */
  rootline: Collection[];
  loading: boolean;
}

const EMPTY_SUB_COLLECTIONS: SubCollectionsState = { items: [], parentId: null, rootline: [], loading: false }

/**
 * One page of a listing's titles, for reading the tree out of their rootlines.
 * Degrades to `[]` — this only ever feeds navigation that is allowed to be absent.
 */
const fetchListingMovies = async (category: string): Promise<MediaEntry[]> => {
  const url = catalogEndpoint({ category })
  try {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`${url} answered ${res.status}`)
    const json = await res.json() as MoviePage
    return json.results ?? []
  } catch (error) {
    console.error(error)
    return []
  }
}

/**
 * The second-level navigation for a collection page, and where that page sits in the
 * tree. `movies` are the titles the page is already showing — read for their
 * rootlines, so the common cases cost no extra request.
 *
 * Three cases, none of them distinguishable from the URL:
 *
 * - the collection has children: they are the row, with nothing marked (you are on
 *   their parent, which the first-level tabs mark).
 * - it is a child: its parent's listing is fetched, the parent's children become the
 *   row, and this collection is the marked one. Without this the row would appear on
 *   a category page and disappear the moment you used it.
 * - neither: no second level, and the page renders as it did before.
 *
 * {@link collectionChildrenEndpoint} is asked first regardless, so the day it starts
 * answering with children it takes over from the rootline reading with no change
 * here.
 */
export const useSubCollections = (id: string | null, movies: MediaEntry[]): SubCollectionsState => {
  const [state, setState] = React.useState<SubCollectionsState>(EMPTY_SUB_COLLECTIONS)

  // Depend on the ids as a string, not on the array: a new array arrives on every
  // render, and as a dependency it would re-run this forever.
  const movieIds = movies.map(movie => movie.id).join(',')

  React.useEffect(() => {
    if (id === null) {
      setState(EMPTY_SUB_COLLECTIONS)
      return
    }
    let active = true
    setState(prev => ({ ...prev, loading: true }))

    const load = async (): Promise<SubCollectionsState> => {
      const rootline = collectionRootline(movies, id)

      const published = await fetchCollectionChildren(id)
      if (published.length > 0) return { items: published, parentId: id, rootline, loading: false }

      const own = subCollectionsOf(movies, id)
      if (own.length > 0) return { items: own, parentId: id, rootline, loading: false }

      // A leaf. Its siblings are the children of its parent, which only the parent's
      // own listing names — this page holds titles from one branch by definition.
      const parent = rootline[rootline.length - 1]
      if (!parent) return { ...EMPTY_SUB_COLLECTIONS, rootline }
      const siblings = subCollectionsOf(await fetchListingMovies(parent.id), parent.id)
      return { items: siblings, parentId: parent.id, rootline, loading: false }
    }

    load().then(next => { if (active) setState(next) })
    return () => { active = false }
    // `movies` is read inside, but `movieIds` is what says it changed — a fresh array
    // of the same titles must not re-run this.
  }, [id, movieIds])

  return state
}

export interface CatalogPageOptions {
  /** Titles per page. Defaults to {@link CATALOG_PAGE_SIZE}. */
  limit?: number;
  /** Ordering. Defaults to {@link CATALOG_DEFAULT_SORT}. */
  sort?: CatalogSort;
}

/**
 * Load one page of the catalogue, optionally narrowed to a collection. Backs both
 * the catalogue index and a collection's own page — they are the same listing with
 * and without a filter.
 */
export const useCatalogPage = (
  category: string | null,
  page: number,
  { limit = CATALOG_PAGE_SIZE, sort = CATALOG_DEFAULT_SORT }: CatalogPageOptions = {}
): CatalogPageState => {
  const [state, setState] = React.useState<CatalogPageState>({
    movies: [], totalPages: null, total: null, hasNext: false, loading: true, failed: false,
  })

  // Derive the URL as a plain string and depend on *that*, so an unrelated re-render
  // can't re-run the request (the same reasoning as the browse showcase rows).
  const requestUrl = catalogEndpoint({ category, page, limit, sort })

  React.useEffect(() => {
    let active = true
    setState(prev => ({ ...prev, loading: true, failed: false }))
    fetch(requestUrl)
      .then(res => {
        if (!res.ok) throw new Error(`${requestUrl} answered ${res.status}`)
        return res.json() as Promise<MoviePage>
      })
      .then(json => {
        if (!active) return
        const movies = json.results ?? []
        // Counters are only meaningful for the unfiltered catalogue (see
        // CatalogPageState.totalPages), and even there they floor — so the page count
        // is computed from the total rather than read from `total_pages`.
        const trustCounters = !category && typeof json.total_results === 'number'
        setState({
          movies,
          totalPages: trustCounters ? Math.max(1, Math.ceil(json.total_results / limit)) : null,
          total: trustCounters ? json.total_results : null,
          hasNext: movies.length >= limit,
          loading: false,
          failed: false,
        })
      })
      .catch(error => {
        console.error(error)
        if (!active) return
        setState({ movies: [], totalPages: null, total: null, hasNext: false, loading: false, failed: true })
      })
    return () => { active = false }
  }, [requestUrl, category, limit])

  return state
}
